export const DEFAULT_TIMEZONE = 'Europe/Belgrade';

export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/Belgrade', label: 'Belgrade (CET/CEST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Warsaw', label: 'Warsaw (CET/CEST)' },
  { value: 'America/New_York', label: 'New York (ET)' },
  { value: 'America/Chicago', label: 'Chicago (CT)' },
  { value: 'America/Denver', label: 'Denver (MT)' },
  { value: 'America/Phoenix', label: 'Phoenix (MST)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PT)' },
  { value: 'America/Anchorage', label: 'Anchorage (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Honolulu (HST)' },
  { value: 'America/Toronto', label: 'Toronto (ET)' },
  { value: 'America/Vancouver', label: 'Vancouver (PT)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AET)' }
];

export type TimezoneOption = { value: string; label: string };

export const getAllTimezoneOptions = (): TimezoneOption[] => {
  const featured = TIMEZONE_OPTIONS;
  const seen = new Set(featured.map((zone) => zone.value));
  let extras: string[] = [];
  try {
    if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl) {
      extras = (Intl as unknown as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf('timeZone');
    }
  } catch {
    extras = [];
  }

  return [
    ...featured,
    ...extras
      .filter((zone) => !seen.has(zone))
      .map((zone) => ({ value: zone, label: zone.replace(/_/g, ' ') }))
  ];
};

export const isValidTimeZone = (value?: string | null): value is string => {
  if (!value) {
    return false;
  }
  try {
    Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
};

export const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

export const resolveTimeZone = (
  preferred?: string | null,
  timezoneAuto?: boolean | null
): string => {
  if (timezoneAuto === false && isValidTimeZone(preferred)) {
    return preferred;
  }
  const device = browserTimeZone();
  return isValidTimeZone(device) ? device : DEFAULT_TIMEZONE;
};

const toDate = (value?: string | Date | null): Date | null => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatWith = (
  value: string | Date | null | undefined,
  timeZone: string,
  options: Intl.DateTimeFormatOptions
): string => {
  const date = toDate(value);
  if (!date) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-US', { timeZone, ...options }).format(date);
};

export const formatDate = (value?: string | Date | null, timeZone = browserTimeZone()): string =>
  formatWith(value, timeZone, { month: 'short', day: 'numeric', year: 'numeric' });

export const formatDateTime = (value?: string | Date | null, timeZone = browserTimeZone()): string =>
  formatWith(value, timeZone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  });

export const formatTime = (value?: string | Date | null, timeZone = browserTimeZone()): string =>
  formatWith(value, timeZone, { hour: 'numeric', minute: '2-digit' });

/** Calendar dates (due dates) without shifting a day across timezones. */
export const formatCalendarDate = (value?: string | Date | null): string => {
  const date = toDate(value);
  if (!date) {
    return '—';
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(date);
};

export const formatNow = (timeZone = browserTimeZone()): string =>
  formatDateTime(new Date(), timeZone);

export const timezoneLabel = (timeZone: string): string =>
  TIMEZONE_OPTIONS.find((option) => option.value === timeZone)?.label || timeZone.replace(/_/g, ' ');
